import angular from 'angular';
import { ngStorage } from 'ngStorage';
import {
    CartAddress,
    CartModel,
    FONT_RIGHT_KEYS,
    FontRightKey,
    FontStyleService,
    ICartAddress,
    ICartAppRights,
    ICartDesktopRights,
    ICartEpubRights,
    ICartFontRight,
    ICartLicense,
    ICartWebRights,
    IDjangoCoupon,
    IFontStyleModel,
    IFoundryModel,
    ILicenseUpgradeDict,
    UserModel,
    getCartRightQty,
    hasOwnProperty,
    isIFontStyle,
} from '../tn.common';
import { GA4Item } from '../tn.common/classes/gtm/GtmService';
import { hasUpgradeOrRenewCartLicenses } from '../tn.licenses/has-license-type.filter/has-upgrade-or-renew-licenses';
import { licensesToUpgradeFrom } from '../tn.licenses/has-license-type.filter/licenses-to-upgrade-from';
import { LicenseListService } from '../tn.licenses/license-list.service/licenseList';
import { LicenseService } from '../tn.licenses/license.service/license-service';
import { UserService } from '../tn.user/service.tn.user';
import { CartRepository } from './cart-repository.service/cartRepository';
import { LocalCarts } from './local-carts.service/localCarts';

export class CouponError extends Error {
    /**
     *
     * @param message
     * @param foundry - if set the error can be resolved by remove all the fonts not belonging to the foundry.
     */
    constructor(message: string, public foundry?: number) {
        super(message);
        this.name = 'CouponError';

        // Set the prototype explicitly.
        // SEE: https://github.com/Microsoft/TypeScript/wiki/Breaking-Changes#extending-built-ins-like-error-array-and-map-may-no-longer-work
        Object.setPrototypeOf(this, CouponError.prototype);
    }
}

function getHostingLabel(right: ICartFontRight, license_type: string): string {
    if (license_type !== 'web') {
        return '';
    }
    if (right.self_hosted) {
        return 'self hosted';
    }
    return 'hosted';
}

function getUnitLabel(license_type: string): string {
    if (license_type === 'desktop') {
        return 'users';
    }
    if (license_type == 'app') {
        return 'apps';
    }
    if (license_type == 'epub') {
        return 'publcations';
    }
    return 'views/month';
}

export function cartRightToGA4Item(
    foundryIdx: Record<number, IFoundryModel>,
    style: IFontStyleModel,
    license_type: FontRightKey,
    right:
        | ICartAppRights
        | ICartDesktopRights
        | ICartEpubRights
        | ICartWebRights,
    coupon?: string
): GA4Item | undefined {
    const rightQty = getCartRightQty(right, license_type);

    // not a valid item if there is no right qty.
    if (!rightQty) return undefined;
    const hosting = getHostingLabel(right, license_type);
    const units = getUnitLabel(license_type);

    const foundry_id = style.canonical_foundry_id;
    const foundry = foundryIdx[foundry_id];
    const item_brand = foundry?.name || foundry_id.toString();

    const item = {
        item_id: style.id.toString(),
        item_name: style.family_name,
        item_variant: style.style_name,
        item_brand,
        item_category: license_type + (hosting ? ' ' + hosting : ''),
        item_category2: right.years ? `${right.years} years` : 'perpetual',
        item_category3: `${rightQty} ${units}`,
        // price: parseFloat(right.price),
        quantity: 1,
        coupon,
    };
    return item;
}

export function cartLicenseToGA4Items(
    foundryIdx: Record<number, IFoundryModel>,
    cartLicense: ICartLicense,
    coupon?: string
): GA4Item[] {
    const items: GA4Item[] = [];
    FONT_RIGHT_KEYS.forEach((rightKey) => {
        if (!cartLicense.fontRights[rightKey]) {
            return;
        }
        const cartRights = cartLicense.fontRights[rightKey];
        const item = cartRightToGA4Item(
            foundryIdx,
            cartLicense.font,
            rightKey,
            cartRights,
            coupon
        );
        item && items.push(item);
    });
    return items;
}

export function cartToGA4Items(
    foundryIdx: Record<number, IFoundryModel>,
    cart: CartModel
): GA4Item[] {
    const items: GA4Item[] = [];
    cart.license.forEach((cartLicense) => {
        const licenseItems = cartLicenseToGA4Items(
            foundryIdx,
            cartLicense,
            cart.coupon?.code || undefined
        );
        items.push(...licenseItems);
    });
    return items;
}

export class CartService {
    static $inject = [
        '$localStorage',
        'tnLicenseListService',
        'tnLicenseService',
        'localCarts',
        'cartRepository',
        'tn.user.service',
        'FontStyleService',
    ];
    protected licensesUpgradeDict: ILicenseUpgradeDict = {};

    constructor(
        protected $localStorage: ngStorage.StorageService,
        protected licenseListService: LicenseListService,
        protected licenseService: LicenseService,
        protected localCarts: LocalCarts,
        protected cartRepository: CartRepository,
        protected userService: UserService,
        protected styleService: FontStyleService
    ) {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        $localStorage.cart = $localStorage.cart || new CartModel();
        // correct a defect introduced that would cause bad cart data to serialize ...
        cartRepository.checkForBadLicenses(this.$localStorage.cart);

        // Clear licensee info on login to prevent contamination from changing users
        this.userService.login$.subscribe(() => {
            this._updateLicensesUpgradeDict();
        });

        this.userService.logout$.subscribe(() => {
            this._emptyLicensee();
            this._updateLicensesUpgradeDict();
        });
    }

    _emptyLicensee() {
        Object.keys(this.$localStorage.cart.licensee).forEach((key) => {
            delete this.$localStorage.cart.licensee[key];
        });
    }

    _updateLicensesUpgradeDict() {
        if (hasUpgradeOrRenewCartLicenses(this.$localStorage.cart.license)) {
            this.licenseService.getLicensesFromServer().then((fullLicenses) => {
                const newDict: ILicenseUpgradeDict = {};
                this.licenseListService
                    .getUpgradeOrRenewalLicenses(
                        this.$localStorage.cart.license
                    )
                    .forEach((license) => {
                        licensesToUpgradeFrom(
                            fullLicenses,
                            this.$localStorage.cart.license
                        )
                            .filter((originalLicense) => {
                                let id = null;

                                const rights = originalLicense.fontRights;
                                Object.keys(rights).forEach((rightType) => {
                                    const licenseId =
                                        license.fontRights[rightType].id;
                                    const right = rights[rightType];
                                    const originalId = right.id || null;
                                    id =
                                        licenseId &&
                                        originalId &&
                                        licenseId === originalId;
                                });
                                return id;
                            })
                            .forEach((originalLicense) => {
                                // aggregate licenses by font id... this will probably overwrite licenses of the same type
                                // for a given style.
                                if (!hasOwnProperty(newDict, license.font.id)) {
                                    newDict[license.font.id] = {
                                        style: license.font,
                                        fontRights: {},
                                    };
                                }

                                Object.keys(originalLicense.fontRights).forEach(
                                    (rightType) => {
                                        newDict[license.font.id].fontRights[
                                            rightType
                                        ] =
                                            originalLicense.fontRights[
                                                rightType
                                            ];
                                    }
                                );
                            });
                    });
                angular.copy(newDict, this.licensesUpgradeDict);
            });
        } else {
            angular.copy({}, this.licensesUpgradeDict);
        }
    }

    static _hasWebHosted(fontRight): boolean {
        return (
            fontRight.web && fontRight.web.views && !fontRight.web.self_hosted
        );
    }

    static _hasWebSelfHosted(fontRight) {
        return (
            fontRight.web && fontRight.web.views && fontRight.web.self_hosted
        );
    }

    static _hasApp(fontRight) {
        return fontRight.app && fontRight.app.apps;
    }

    static _hasDesktop(fontRight) {
        return fontRight.desktop && fontRight.desktop.computers;
    }

    static _hasEpub(fontRight) {
        return fontRight.epub && fontRight.epub.ebooks;
    }

    static _hasBroadcast(fontRight) {
        return fontRight.broadcast && fontRight.broadcast.broadcast;
    }

    static _hasServer(fontRight) {
        return fontRight.server && fontRight.server.servers;
    }

    static _hasGame(fontRight) {
        return fontRight.game && fontRight.game.games;
    }

    static _hasEmbedding(fontRight) {
        return fontRight.embedding && fontRight.embedding.embedding;
    }

    static _listHasRight(licenseList, predicate) {
        if (Array.isArray(licenseList)) {
            return licenseList.some(function (license) {
                return predicate(license.fontRights);
            });
        } else {
            return Object.keys(licenseList).some(function (licenseIndex) {
                return predicate(licenseList[licenseIndex].fontRights);
            });
        }
    }

    public getLicenseTypes(license) {
        const fontRight = license.fontRights;
        const types: string[] = [];

        if (CartService._hasWebHosted(fontRight)) {
            types.push('web');
        }
        if (CartService._hasWebSelfHosted(fontRight)) {
            types.push('web_self_hosted');
        }
        if (CartService._hasApp(fontRight)) {
            types.push('app');
        }
        if (CartService._hasDesktop(fontRight)) {
            types.push('desktop');
        }
        if (CartService._hasEpub(fontRight)) {
            types.push('epub');
        }

        return types;
    }

    public getCart() {
        return this.$localStorage.cart;
    }

    public updateStyleDetails() {
        const styleIds = this.$localStorage.cart.license.map(
            (license) => license.font.id
        );
        this.styleService
            .list({ id: styleIds, page_size: styleIds.length + 1 })
            .then(({ results: styles }) => {
                styles.forEach((style) => {
                    const result = this.$localStorage.cart.license.filter(
                        (license) => license.font.id === style.id
                    );
                    result[0].font = style;
                });
            });
    }

    public removeFontRightsFromCart(licenses, upgradeOrRenew) {
        this.licenseListService.removeFontRights(
            this.$localStorage.cart.license,
            licenses,
            upgradeOrRenew
        );
        this._updateLicensesUpgradeDict();
    }

    public removeFromCart(licenses) {
        this.licenseListService.removeLicense(
            this.$localStorage.cart.license,
            licenses
        );
        this._updateLicensesUpgradeDict();
    }

    public editLicense(licensesToEdit, licenseOptions, allowRemoval = false) {
        this.licenseListService.editLicenses(
            this.$localStorage.cart.license,
            licensesToEdit,
            licenseOptions,
            null,
            allowRemoval
        );
        this._updateLicensesUpgradeDict();
    }

    public getLicensesUpgradeDict() {
        this._updateLicensesUpgradeDict();
        return this.licensesUpgradeDict;
    }

    public mapUserToCartAddress(
        user: UserModel = new UserModel()
    ): ICartAddress {
        const first_name = user.first_name || '';
        const last_name = user.last_name || '';
        return {
            address1: user.address1 || '',
            address2: user.address2 || '',
            city: user.city || '',
            state: user.state || '',
            zipcode: user.zipcode || '',
            country: user.country || '',
            name: `${first_name} ${last_name}`,
            first_name,
            last_name,
            company: user.company || '',
        };
    }

    public setLicensee(addressOrUser: ICartAddress): void {
        if (!this.validateLicensee(addressOrUser)) {
            throw new Error('Invalid Licensee');
        }
        const licensee: ICartAddress = {
            address1: addressOrUser.address1,
            address2: addressOrUser.address2,
            city: addressOrUser.city,
            state: addressOrUser.state,
            zipcode: addressOrUser.zipcode,
            country: addressOrUser.country,
            name: addressOrUser.name,
            first_name: addressOrUser.first_name,
            last_name: addressOrUser.last_name,
            company: addressOrUser.company,
        };
        this.$localStorage.cart.licensee = licensee;
    }

    public getLicensee(): ICartAddress {
        return this.$localStorage.cart.licensee;
    }

    public isLicensee(candidate: ICartAddress): boolean {
        const licensee = this.getLicensee();
        return (
            licensee &&
            licensee.address1 === candidate.address1 &&
            licensee.address2 === candidate.address2 &&
            licensee.city === candidate.city &&
            licensee.state === candidate.state &&
            licensee.zipcode === candidate.zipcode &&
            licensee.country === candidate.country &&
            licensee.first_name === candidate.first_name &&
            licensee.last_name === candidate.last_name &&
            licensee.company === candidate.company &&
            licensee.vat == candidate.vat
        );
    }

    public validateLicensee(licensee: ICartAddress): boolean {
        return !!(
            licensee.first_name &&
            licensee.last_name &&
            licensee.address1 &&
            licensee.city &&
            licensee.zipcode &&
            licensee.country
        );
    }

    public isLicenseeComplete(): boolean {
        const licensee = this.$localStorage.cart.licensee;
        return this.validateLicensee(licensee);
    }

    public stylesInCart(
        styles: IFontStyleModel | IFontStyleModel[] | number | number[]
    ): boolean {
        if (styles === undefined || styles === null) {
            return false;
        }

        // normalize to array.
        const _styles = Array.isArray(styles) ? styles : [styles];
        return _styles.every((style) => {
            const id = isIFontStyle(style) ? style.id : style;
            // ensure int, even if we were passed a string for someon reason.
            // TODO: remove once we are sure we are always passing numbers.
            const styleId = parseInt(id.toString());
            return this.$localStorage.cart.license.some((license) => {
                return license.font.id === styleId;
            });
        });
    }

    public setCart(newCart) {
        this.emptyCart();
        // Only copy licenses and title
        if (newCart.title) {
            this.$localStorage.cart.title = newCart.title;
        }

        this.licenseListService.addLicenses(
            this.$localStorage.cart.license,
            newCart.license
        );
        this._updateLicensesUpgradeDict();
    }

    public applyCoupon(coupon: IDjangoCoupon): void {
        if (coupon.expired) {
            throw new CouponError(`Coupon ${coupon.code} has expired.`);
        }

        // validate % discount coupons
        if (coupon.discount_type == 1) {
            if (coupon.foundry) {
                if (
                    !this.$localStorage.cart.license.every(
                        (license) =>
                            license.font.foundry.indexOf(coupon.foundry) !== -1
                    )
                ) {
                    // indicates whether splitting the cart would allow the coupon to apply
                    const split = this.$localStorage.cart.license.some(
                        (license) =>
                            license.font.foundry.indexOf(coupon.foundry) !== -1
                    );
                    const foundry = split ? coupon.foundry : undefined;
                    throw new CouponError(
                        `Coupon ${coupon.code} is not valid for the fonts in your cart.`,
                        foundry
                    );
                }
            }
        }

        // validate $ discount coupons
        if (coupon.discount_type == 0) {
            const familyMatched = this.$localStorage.cart.license.some(
                (license) =>
                    coupon.family && license.font.family === coupon.family
            );
            const foundryMatched = this.$localStorage.cart.license.some(
                (license) =>
                    coupon.foundry &&
                    license.font.foundry.indexOf(coupon.foundry) !== -1
            );

            if (
                coupon.family &&
                coupon.foundry &&
                !familyMatched &&
                !foundryMatched
            ) {
                throw new CouponError(
                    `This coupon is only valid for the family ${coupon.family_name} or fonts from ${coupon.foundry_name}`
                );
            }
            if (coupon.family && !familyMatched) {
                throw new CouponError(
                    `This coupon is only valid for the family ${coupon.family_name}`
                );
            }
            if (coupon.foundry && !foundryMatched) {
                throw new CouponError(
                    `This coupon is only valid on fonts from ${coupon.foundry_name}`
                );
            }
        }
        this.$localStorage.cart.coupon = coupon;
    }

    public isInvoiced(localCart = this.$localStorage.cart) {
        return (
            localCart.payments.length !== 0 &&
            localCart.payments[0].provider === 1
        );
    }

    public hasWebLicenseHosted(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasWebHosted
        );
    }

    public hasWebLicenseSelfHosted(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasWebSelfHosted
        );
    }

    public hasAppLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasApp
        );
    }

    public hasEpubLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasEpub
        );
    }

    public hasDesktopLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasDesktop
        );
    }

    public hasBroadcastLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasBroadcast
        );
    }

    public hasServerLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasServer
        );
    }

    public hasGameLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasGame
        );
    }

    public hasEmbeddingLicense(localCart = this.$localStorage.cart) {
        return CartService._listHasRight(
            localCart.license,
            CartService._hasEmbedding
        );
    }

    public hasDownloadLicense(localCart = this.$localStorage.cart) {
        return this.licenseListService.hasDownloadLicense(localCart.license);
    }

    public getMissingLicenses(localCart = this.$localStorage.cart) {
        return this.licenseListService.getMissingLicenses(localCart.license);
    }

    public emptyCart() {
        if (hasOwnProperty(this.$localStorage.cart, 'card')) {
            delete this.$localStorage.cart.card;
        }
        if (hasOwnProperty(this.$localStorage.cart, 'coupon')) {
            delete this.$localStorage.cart.coupon;
        }
        if (hasOwnProperty(this.$localStorage.cart, 'stripeToken')) {
            delete this.$localStorage.cart.stripeToken;
        }
        if (hasOwnProperty(this.$localStorage.cart, 'billingAddress')) {
            delete this.$localStorage.cart.billingAddress;
        }
        // this._emptyLicensee();
        this.$localStorage.cart.license.splice(
            0,
            this.$localStorage.cart.license.length
        );
        this.$localStorage.cart.id = null;
    }

    public splitCartLicenses(foundry) {
        const saveLicenses = this.$localStorage.cart.license.filter(
            (license): boolean => !license.font.foundry.includes(foundry)
        );
        const newCart = Object.assign({}, this.$localStorage.cart);
        newCart.license = saveLicenses;
        newCart.coupon = null;
        this.$localStorage.cart.license =
            this.$localStorage.cart.license.filter((license): boolean =>
                license.font.foundry.includes(foundry)
            );

        const cartFamilies = new Set(
            saveLicenses.map((license): string => license.font.family_name)
        );
        const currentDate = new Date();
        this.saveCartToServer(
            `${currentDate.toISOString().substring(0, 10)} - ${Array.from(
                cartFamilies
            ).join(' - ')}`,
            newCart
        );
    }

    public clearCoupon() {
        delete this.$localStorage.cart.coupon;
    }

    public saveCartToServer(title, currentSavedCart = null) {
        let finalCart;
        if (currentSavedCart) {
            finalCart = angular.copy(currentSavedCart);
        } else {
            finalCart = angular.copy(this.$localStorage.cart);
        }
        finalCart.title = title;
        return this.localCarts.saveCart(finalCart);
    }

    public updateCartOnServer(newCart) {
        if (newCart.id) {
            return this.localCarts.updateCart(newCart);
        }
        return this.localCarts.saveCart(newCart);
    }

    // Corresponds to the payment information
    public setBillingAddress(billingAddress: ICartAddress): void {
        this.$localStorage.cart.billingAddress = billingAddress;
    }

    // should this billing address be treated separately from the card billing address? Should it be just
    // for invoiced accounts, otherwise we use the card address details?
    public getBillingAddress(): ICartAddress {
        if (!this.$localStorage.cart.billingAddress) {
            this.$localStorage.cart.billingAddress = new CartAddress();
        }
        return this.$localStorage.cart.billingAddress;
    }

    public clearCard() {
        delete this.$localStorage.cart.card;
    }

    // To identify what card to use on the customer object
    public setCard(card) {
        this.$localStorage.cart.card = card;
    }

    public clearStripeToken() {
        delete this.$localStorage.cart.stripeToken;
    }

    // Single use token
    public setStripeToken(token) {
        this.$localStorage.cart.stripeToken = token;
    }

    public setInvoice(bool) {
        this.$localStorage.cart.invoice = bool;
    }

    public countLicenseTypes(licenses) {
        const countTypes = {
            web: false,
            desktop: false,
            epub: false,
            app: false,
        };
        const fontRights = licenses.map((license) => license.fontRights);
        fontRights.forEach((fontRight) => {
            if (!countTypes.web && CartService._hasWebHosted(fontRight)) {
                countTypes.web = true;
            }
            if (!countTypes.web && CartService._hasWebSelfHosted(fontRight)) {
                countTypes.web = true;
            }
            if (!countTypes.app && CartService._hasApp(fontRight)) {
                countTypes.app = true;
            }
            if (!countTypes.desktop && CartService._hasDesktop(fontRight)) {
                countTypes.desktop = true;
            }
            if (!countTypes.epub && CartService._hasEpub(fontRight)) {
                countTypes.epub = true;
            }
        });

        return Object.values(countTypes).filter((type) => type).length;
    }
}
